// Implements: F-UIX-018, F-UIX-014, F-OBS-011, F-IDN-027
/**
 * The backoffice shell (F-UIX-018): one route group under `/backoffice` and the registry of its
 * sections.
 *
 * **Extension point.** A later section (consumption by scope, distribution, audit, the
 * platform views) is ONE entry in `BACKOFFICE_SECTIONS`: a path segment, a dictionary key
 * for its label, its audience (`admits`) and its page. The entry is a route that renders the
 * not-found page for everybody it does not admit, so a section a person may not see is not
 * reachable by typing its address; the sidebar (`app/layout/navigation.ts`) offers the same
 * sections from the same permission answer, and since UX-10 it is the only navigation: the
 * backoffice no longer draws one of its own. The server still decides every read (the audience
 * authorises nothing). Money is shown only inside these sections (OD-12, UIX.9).
 *
 * The group sits inside `RequireSession` with every other guarded route, so the backoffice
 * is drawn in the same `AppShell`.
 */

import type { ReactElement } from "react";
import { Navigate, Outlet } from "react-router";
import type { RouteObject } from "react-router";

import { BACKOFFICE_PATH, admits } from "../modules/backoffice/model/sections";
import type { BackofficeAudience } from "../modules/backoffice/model/sections";
import { useSession } from "../shared/api/session";
import type { TranslationKey } from "../shared/i18n";
import type { BackofficeScope } from "../types/api";
import { AuditPage } from "./pages/AuditPage";
import { ConsumptionPage } from "./pages/ConsumptionPage";
import { DistributionPage } from "./pages/DistributionPage";
import { InvitationsPage } from "./pages/InvitationsPage";
import { MyUsagePage } from "./pages/MyUsagePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PeoplePage } from "./pages/PeoplePage";
import { TeamDetailPage } from "./pages/TeamDetailPage";
import { TeamsPage } from "./pages/TeamsPage";

export interface BackofficeSection {
  readonly id: string;
  /** The segment under `/backoffice`. */
  readonly path: string;
  /** A `backoffice.nav.*` key: a nav label takes no parameter. */
  readonly labelKey: Extract<TranslationKey, `backoffice.nav.${string}`>;
  readonly audience: BackofficeAudience;
  readonly element: ReactElement;
}

/** Every section of the backoffice, in nav order. The first one is where `/backoffice` lands. */
export const BACKOFFICE_SECTIONS: readonly BackofficeSection[] = [
  { id: "usage", path: "usage", labelKey: "backoffice.nav.myUsage", audience: "everyone", element: <MyUsagePage /> },
  {
    id: "consumption",
    path: "consumption",
    labelKey: "backoffice.nav.consumption",
    // Whoever the server lets read an organization's or a team's consumption (UIX.5): the org
    // admin and the team lead; a member sees only My usage.
    audience: ["usage:read_organization", "usage:read_team"],
    element: <ConsumptionPage />,
  },
  {
    id: "distribution",
    path: "distribution",
    labelKey: "backoffice.nav.distribution",
    // Whoever the server lets distribute an organization's budget (the editor itself follows the
    // policy view's `can_manage`) or read a team's: the team lead reads the own teams' allocations.
    audience: ["usage_allocation:manage", "usage:read_team"],
    element: <DistributionPage />,
  },
  {
    id: "audit",
    path: "audit",
    labelKey: "backoffice.nav.audit",
    // Only whom the server lets read the organization's trail (UIX.7, OBS.8): its org admin. The
    // team lead, a member and the platform admin are neither offered it nor can open its address.
    audience: ["audit:read_organization"],
    element: <AuditPage />,
  },
  // Mockup v3 P3: the organization's people, teams and invitations (F-IDN-027).
  {
    id: "people",
    path: "people",
    labelKey: "backoffice.nav.people",
    audience: ["member:update_role"],
    element: <PeoplePage />,
  },
  {
    id: "teams",
    path: "teams",
    labelKey: "backoffice.nav.teams",
    // The org admin creates teams; a team lead adds people to the own team.
    audience: ["team:create", "team_member:add"],
    element: <TeamsPage />,
  },
  {
    id: "team",
    path: "teams/:teamId",
    labelKey: "backoffice.nav.teams",
    audience: ["team:create", "team_member:add"],
    element: <TeamDetailPage />,
  },
  {
    id: "invitations",
    path: "invitations",
    labelKey: "backoffice.nav.invitations",
    audience: ["invitation:issue"],
    element: <InvitationsPage />,
  },
];

/** The address of a section. */
export function sectionHref(section: BackofficeSection): string {
  return `${BACKOFFICE_PATH}/${section.path}`;
}

/** The session's scopes, or none while there is no session. */
function useScopes(): readonly BackofficeScope[] {
  const { state } = useSession();
  return state.status === "authenticated" ? state.view.scopes : [];
}

/** A section's page for the people it admits; the not-found page for everybody else. */
export function SectionGate({ section }: { readonly section: BackofficeSection }): ReactElement {
  const scopes = useScopes();
  return admits(section.audience, scopes) ? section.element : <NotFoundPage />;
}

/** The route group, mounted by `router.tsx` inside the session guard. */
export function backofficeRoute(sections: readonly BackofficeSection[] = BACKOFFICE_SECTIONS): RouteObject {
  const first = sections[0];
  return {
    path: BACKOFFICE_PATH,
    element: <Outlet />,
    children: [
      ...(first === undefined ? [] : [{ index: true, element: <Navigate to={first.path} replace /> }]),
      ...sections.map((section) => ({ path: section.path, element: <SectionGate section={section} /> })),
    ],
  };
}
